import React from 'react';
import { useTranslation } from 'react-i18next';

import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.js';
import {
  ItemCard,
  ItemCardDivider,
  ItemCardFooter,
} from '@sola/ark-core/molecules/item-card/item-card.js';
import { cn } from '@sola/ark-core/utils/helpers.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import '@sola/locales/formatters.js';
import { GraphQLCommitment } from '@sola/schema/dotcom/supporters/schema.generated.js';

import { CommitmentDetailsSheet } from '../../organisms/commitments-details-sheet/commitment-details-sheet';
import { CommitmentCardInfoSection } from '../commitment-card-info-section/commitment-card-info-section.js';

export type CommitmentCardProps = {
  commitment: GraphQLCommitment;
  isActive?: boolean;
  onActiveChange?: (open: boolean, commitmentId: string) => void;
};

/**
 * Commitment card, shown for both commitments and giving history items.
 */
export const CommitmentCard: React.FC<CommitmentCardProps> = (props) => {
  const { t } = useTranslation(['my', 'common']);
  const { commitment, isActive, onActiveChange } = props;

  return (
    <ItemCard>
      <CommitmentCardInfoSection commitment={commitment} />
      {/* Footer */}
      <ItemCardDivider />
      <ItemCardFooter justify="between">
        <span
          className={cn(
            'flex items-center label-moderate label-neutral-mild label-sm md:label-md',
            {
              'label-warning': !commitment.isRecurring,
            },
          )}
        >
          {!commitment.isRecurring && (
            <>
              <ArkIcon
                iconName="alert-triangle"
                className="mr-snug-lg"
                size="1.25rem"
              />
              <span>{t('my:commitments.notRecurring.title')}</span>
            </>
          )}

          {commitment.isRecurring &&
            // Protect against giving summary timing out
            !!commitment.paymentDateNext &&
            t('my:commitments.nextPayment', {
              date: commitment.paymentDateNext,
            })}
        </span>

        <CommitmentDetailsSheet
          commitment={commitment!}
          isOpen={isActive}
          onOpenChange={onActiveChange}
          openTrigger={
            <div className="ml-auto">
              <LinkStyle
                noUnderline
                noPadding
                inline
                variant="primary"
                size="lg"
              >
                <button type="button" className="CommitmentCard__trigger">
                  {t('commitments.detailsSheet.triggerLabel')}
                </button>
              </LinkStyle>
            </div>
          }
        />
      </ItemCardFooter>
    </ItemCard>
  );
};
